/**
 * useBrowserSignInPhase — the ONE hook every surface reads to say where a sign-in in Prest0n's browser
 * stands (slice E of the browser sign-in program, "one flow on every surface"; Miles's ruling R1: a
 * sign-in button stays usable until the sign-in is confirmed).
 *
 * Slice D gave the chat's sign-in card one state model (browserSignInPhase.ts) and one table of words
 * (browserSignInPhaseCopy.ts). This hook puts the Integrations page (ConnectedSection, AppTile, the legal
 * apps view) and onboarding's "continue signing in" row on the SAME store and words, so every surface
 * says the same thing at the same moment:
 *
 *   | phase        | button                  | status line                         |
 *   | not started  | "sign in to {app}"      | none                                |
 *   | opening      | "opening…"              | "starting Prest0n's secure browser…"|
 *   | open         | "continue in the window"| "sign-in window open"               |
 *   | not finished | "continue signing in"   | "sign-in not finished"              |
 *   | signed in    | "signed in ✓" (inert)   | "signed in"                         |
 *   | signed out   | "sign in again"         | "signed out — sign in again"        |
 *
 * Which phase (`resolveBrowserSignInPhase`, pure): a window on its way or open, or a sign-in the person
 * just finished, wins over the record (it is newer than any list the page holds); otherwise the record's
 * status (connected, signin-lost, registered); otherwise anything else this page reported; otherwise not
 * started.
 *
 * `inert` is true only once signed in: a press then does nothing. A press in any other phase goes to the
 * one flow (browserSignInStart.ts), which brings an open window forward instead of opening a second one.
 */
import { createElement, Fragment, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import {
  browserSignInKey,
  browserSignInPhaseFromRecord,
  getBrowserSignInPhase,
  subscribeBrowserSignInPhase,
  type BrowserSignInPhase,
  type BrowserSignInPhaseState,
} from './browserSignInPhase';
import {
  browserSignInButtonWords,
  browserSignInStatusWords,
  type BrowserSignInLang,
} from './browserSignInPhaseCopy';

export interface BrowserSignInView {
  phase: BrowserSignInPhase;
  /** The button's words. */
  button: string;
  /** The line beside the button ('' = none). */
  status: string;
  /** Signed in: the button does nothing. */
  inert: boolean;
  /** The window is on its way (a busy hint for the button). */
  busy: boolean;
}

/** The phases a live report always wins with, whatever the record says. */
const LIVE_WINS: readonly BrowserSignInPhase[] = ['opening', 'open', 'signed-in'];

/** Which phase a surface shows, from this page's live report and the connection's record. */
export function resolveBrowserSignInPhase(
  live: Pick<BrowserSignInPhaseState, 'phase'> | null | undefined,
  recordStatus: unknown,
): BrowserSignInPhase {
  if (live && LIVE_WINS.includes(live.phase)) return live.phase;
  const fromRecord = browserSignInPhaseFromRecord(recordStatus);
  if (fromRecord) return fromRecord;
  if (live) return live.phase;
  return 'not-started';
}

/** The words and flags for a phase. */
export function browserSignInView(phase: BrowserSignInPhase, app: string, lang: BrowserSignInLang = 'en'): BrowserSignInView {
  return {
    phase,
    button: browserSignInButtonWords(phase, lang, app),
    status: browserSignInStatusWords(phase, lang),
    inert: phase === 'signed-in',
    busy: phase === 'opening',
  };
}

/**
 * Where the sign-in to `appKey` (the app's name; matched by the store's one key) stands, kept current
 * with every report.
 */
export function useBrowserSignInPhase(
  appKey: string,
  record?: { status?: unknown } | null,
  opts: { lang?: BrowserSignInLang } = {},
): BrowserSignInView {
  const key = browserSignInKey(appKey);
  const [live, setLive] = useState<BrowserSignInPhaseState | null>(() => getBrowserSignInPhase(appKey));
  useEffect(() => {
    setLive(getBrowserSignInPhase(appKey));
    if (!key) return undefined;
    return subscribeBrowserSignInPhase((state) => {
      if (state.appKey === key) setLive(state);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const phase = resolveBrowserSignInPhase(live, record?.status);
  return browserSignInView(phase, appKey, opts.lang);
}

/**
 * The hook as a component, for a surface that draws its rows in a loop (the legal apps view): the
 * phase's view goes to `children`.
 */
export function WithBrowserSignInPhase(props: {
  appKey: string;
  record?: { status?: unknown } | null;
  lang?: BrowserSignInLang;
  children: (view: BrowserSignInView) => ReactNode;
}): ReactElement {
  const view = useBrowserSignInPhase(props.appKey, props.record, { lang: props.lang });
  return createElement(Fragment, null, props.children(view));
}
